<script lang="ts">
  // The message reader (spec §10). Data: on folder/uid change the state resets, the cached row +
  // body render immediately, then `groups.latest('message', …)` fetches the full message so a
  // rapid A→B→C only ever renders C. `markSeenOnOpen` is the router's job (not duplicated here).
  import { untrack } from 'svelte';
  import { api, ApiError, groups, isAborted } from '$lib/api/client';
  import type { ApiAttachment, MessageDetail } from '$lib/api/types';
  import { formatDate, t } from '$lib/i18n';
  import { inboxPath } from '$lib/mail/folders';
  import { initialOf } from '$lib/mail/format';
  import { labelsOf } from '$lib/mail/labels';
  import { buildForwardDraft, buildReplyDraft, isFromMe, quoteHtml, recipientRole } from '$lib/mail/reply';
  import { sanitizeReader, textToHtml, unblockImages } from '$lib/mail/sanitize';
  import { isSnoozedFolder } from '$lib/mail/snooze';
  import { confirm, prompt, store } from '$lib/store';
  import type { Address, MessageRow } from '$lib/types';
  import { anchorFromElement, type MenuAnchor } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import Attachments from './Attachments.svelte';
  import AuthBanner from './AuthBanner.svelte';
  import Details from './Details.svelte';
  import EmlPreview from './EmlPreview.svelte';
  import ImagesNote from './ImagesNote.svelte';
  import LabelsBar from './LabelsBar.svelte';
  import MessageBody from './MessageBody.svelte';
  import MutedBar from './MutedBar.svelte';
  import ReaderMenu from './ReaderMenu.svelte';
  import ThreadItem from './ThreadItem.svelte';
  import UnsubBar from './UnsubBar.svelte';
  import Viewer from './Viewer.svelte';
  import { cacheBody, inlineParts, readCached, SKELETON_BARS, writeFlags, type MenuAction, type ViewerSpec } from './_local';

  let { folder, uid, split = false }: { folder: string; uid: number; split?: boolean } = $props();

  /** A local action younger than this keeps its optimistic flags over the fetched ones. */
  const RECENT_ACT_MS = 2000;

  interface Meta {
    subject: string;
    fromName: string;
    fromAddr: string;
    date: string;
  }
  type FailKind = 'msgGone' | 'offline' | 'netError';

  // ---- per-message state ----------------------------------------------------------------------
  let meta = $state<Meta | null>(null);
  let attachments = $state<ApiAttachment[]>([]);
  let from = $state<Address[]>([]);
  let to = $state<Address[]>([]);
  let cc = $state<Address[]>([]);
  let replyTo = $state<Address[]>([]);
  let messageId = $state<string | null>(null);
  let references = $state<string[]>([]);
  let auth = $state<MessageDetail['auth']>(null);
  let flags = $state<string[] | null>(null);
  let muted = $state<{ id: string } | null>(null);
  let unsub = $state<MessageDetail['unsubscribe']>(null);
  let fromAttached = $state(false);
  let html = $state<string | null>(null);
  let isHtml = $state(false);
  let blocked = $state(0);
  let loading = $state(true);
  let failed = $state<FailKind | null>(null);
  let gone = $state(false);
  let details = $state(false);
  let menuOpen = $state(false);
  let menuAnchor = $state<MenuAnchor | null>(null);
  let viewer = $state<ViewerSpec | null>(null);
  let threadOpenAll = $state<boolean | null>(null);
  let bodyEl = $state<ReturnType<typeof MessageBody> | undefined>();
  let article = $state<HTMLElement | null>(null);
  let seq = 0;

  // ---- derived -------------------------------------------------------------------------------
  const liveRow = $derived(store.msgs.find((m) => m.uid === uid && m.folder === folder) ?? null);
  const curFlags = $derived(liveRow?.flags ?? flags ?? []);
  const flagged = $derived(curFlags.includes('\\Flagged'));
  const labels = $derived(labelsOf(curFlags));
  const canArchive = $derived(!!store.archivePath && folder !== store.archivePath);
  const isSnoozed = $derived(isSnoozedFolder(folder));
  const isJunk = $derived(store.junkPath() === folder);
  const me = $derived(store.user ?? '');
  const fromMe = $derived(isFromMe(meta?.fromAddr, me));
  const role = $derived(recipientRole(to, cc, me));
  /** Newest-first siblings of the open message (empty when it stands alone). */
  const thread = $derived(store.threadOf(uid, folder));
  const idx = $derived(thread.findIndex((r) => r.uid === uid));
  /** Older siblings above the message, newer ones below — both in reading (chronological) order. */
  const older = $derived(idx >= 0 ? thread.slice(idx + 1).reverse() : []);
  const newer = $derived(idx >= 0 ? thread.slice(0, idx).reverse() : []);
  const srcUrl = $derived(api.sourceUrl(folder, uid));
  const dlUrl = $derived(api.sourceUrl(folder, uid, true));

  // ---- load on folder/uid change ---------------------------------------------------------------
  $effect(() => {
    const f = folder;
    const u = uid;
    untrack(() => {
      reset();
      void load(f, u, ++seq);
      autoFocus();
    });
  });

  // The shell closes the viewer through `store.viewerOpen` (Escape handler).
  $effect(() => {
    if (!store.viewerOpen && viewer) viewer = null;
  });

  function reset(): void {
    meta = null;
    attachments = [];
    from = [];
    to = [];
    cc = [];
    replyTo = [];
    messageId = null;
    references = [];
    auth = null;
    flags = null;
    muted = null;
    unsub = null;
    fromAttached = false;
    html = null;
    isHtml = false;
    blocked = 0;
    loading = true;
    failed = null;
    gone = false;
    details = false;
    menuOpen = false;
    menuAnchor = null;
    viewer = null;
    threadOpenAll = null;
  }

  /** Focus the article once per message when nothing else has focus (never over the viewer). */
  function autoFocus(): void {
    if (typeof requestAnimationFrame === 'undefined') return;
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (!store.viewerOpen && article && (!active || active === document.body)) article.focus({ preventScroll: true });
    });
  }

  function applyRow(r: MessageRow): void {
    meta = { subject: r.subject, fromName: r.fromName, fromAddr: r.fromAddr, date: r.date };
    if (!flags) flags = [...r.flags];
    if (!messageId) messageId = r.messageId;
    if (!references.length) references = [...(r.references ?? [])];
  }

  function applyBody(f: string, u: number, type: string, content: string, atts: readonly ApiAttachment[]): void {
    if (type === 'text/html') {
      const r = sanitizeReader(content, inlineParts(f, u, atts));
      html = r.html;
      isHtml = true;
      blocked = r.blockedImages;
    } else {
      html = textToHtml(content);
      isHtml = false;
      blocked = 0;
    }
    failed = null;
    gone = false;
  }

  function applyDetail(f: string, u: number, m: MessageDetail): void {
    const sender = m.from?.[0];
    meta = { subject: m.subject ?? '', fromName: sender?.name || '', fromAddr: sender?.address || '', date: m.date };
    from = m.from ?? [];
    to = m.to ?? [];
    cc = m.cc ?? [];
    replyTo = m.replyTo ?? [];
    messageId = m.messageId ?? null;
    references = m.references ?? [];
    auth = m.auth ?? null;
    flags = m.flags ?? null;
    muted = m.muted ?? null;
    unsub = m.unsubscribe ?? null;
    attachments = m.attachments ?? [];
    fromAttached = !!m.body?.fromAttached;
    alignRowFlags(f, u, m.flags ?? []);
    if (m.body) applyBody(f, u, m.body.type, m.body.content, attachments);
    else {
      html = '';
      isHtml = false;
      blocked = 0;
      failed = null;
    }
  }

  /**
   * The fetched message carries the server's flags; when the cached row disagrees (a label or
   * star set elsewhere) the row is aligned so chips/star read the truth — unless a local action
   * is in flight, whose optimistic flags must win until the next sync.
   */
  function alignRowFlags(f: string, u: number, serverFlags: readonly string[]): void {
    const row = store.msgs.find((m) => m.uid === u && m.folder === f);
    if (!row || Date.now() - store.lastLocalAct < RECENT_ACT_MS) return;
    const same = row.flags.length === serverFlags.length && row.flags.every((x) => serverFlags.includes(x));
    if (!same) void writeFlags(f, u, serverFlags);
  }

  async function load(f: string, u: number, mySeq: number): Promise<void> {
    const cached = await readCached(f, u);
    if (mySeq !== seq) return;
    if (cached.row) applyRow(cached.row);
    if (cached.body) {
      attachments = cached.body.attachments;
      fromAttached = cached.body.fromAttached;
      applyBody(f, u, cached.body.type, cached.body.content, cached.body.attachments);
    }
    if (store.offline) {
      loading = false;
      if (!cached.body) failed = 'offline';
      return;
    }
    try {
      const m = await groups.latest('message', (signal) => api.message(f, u, { signal }));
      if (mySeq !== seq) return;
      applyDetail(f, u, m);
      void cacheBody(f, u, m);
    } catch (err) {
      if (isAborted(err) || mySeq !== seq) return;
      if (err instanceof ApiError && err.status === 404) {
        gone = true;
        failed = 'msgGone';
        html = null;
      } else if (html === null) {
        failed = store.offline ? 'offline' : 'netError';
      }
    } finally {
      if (mySeq === seq) loading = false;
    }
  }

  // ---- body / images ---------------------------------------------------------------------------
  function onRendered(root: ShadowRoot): void {
    if (blocked > 0 && meta && store.isTrusted(meta.fromAddr)) {
      unblockImages(root);
      blocked = 0;
    }
  }

  function showImages(): void {
    bodyEl?.unblock();
    blocked = 0;
  }

  async function alwaysImages(): Promise<void> {
    if (!meta?.fromAddr) return;
    await store.trustSender(meta.fromAddr);
    bodyEl?.unblock();
    blocked = 0;
  }

  // ---- reply / forward (spec §10.2) ------------------------------------------------------------
  function quotedHtml(): string {
    const d = formatDate(meta?.date ?? '');
    const n = meta?.fromName || meta?.fromAddr || '';
    return quoteHtml(t('quoteHeader', { d, n }), bodyEl?.text() ?? '');
  }

  function reply(all: boolean): void {
    if (!meta) return;
    store.openCompose(
      buildReplyDraft({ subject: meta.subject, from, replyTo, to, cc, messageId, references, fromAddr: meta.fromAddr }, { me, all, quoted: quotedHtml() }),
    );
  }

  function forward(): void {
    if (!meta) return;
    store.openCompose(buildForwardDraft({ subject: meta.subject, attachments }, quotedHtml(), folder, uid));
  }

  // ---- leave actions: compute the neighbour first, act, then advance ----------------------------
  async function leaveWith(fn: () => Promise<unknown>): Promise<void> {
    const next = store.nextAfter();
    await fn();
    store.afterAction(next);
  }

  async function doArchive(): Promise<void> {
    if (!canArchive) return;
    await leaveWith(() => store.archive(store.threadUids(uid, folder), folder));
  }

  async function doDelete(): Promise<void> {
    if (store.isPurge(folder)) {
      const ok = await confirm({ title: t('confirmTitle'), body: t('confirmPurge'), ok: t('del'), danger: true });
      if (!ok) return;
    }
    await leaveWith(() => store.remove(store.threadUids(uid, folder), folder));
  }

  async function doUnsnooze(): Promise<void> {
    await leaveWith(() => store.unsnooze([uid]));
  }

  function doSnooze(): void {
    store.snoozeTarget = [uid];
  }

  async function doMarkUnread(): Promise<void> {
    await leaveWith(() => store.act('unseen', folder, [uid], undefined, { undoable: true }));
  }

  function doMove(): void {
    store.selectOnly(uid);
    store.moveSheet = true;
    store.backToList();
  }

  async function doJunk(): Promise<void> {
    const dest = isJunk ? inboxPath(store.folders) : store.junkPath();
    if (!dest || dest === folder) {
      store.showToast('actionFailed');
      return;
    }
    await leaveWith(() => store.act('move', folder, store.threadUids(uid, folder), dest, { undoable: true }));
  }

  function toggleStar(): void {
    void store.act(flagged ? 'unflag' : 'flag', folder, [uid]);
  }

  function fromSender(): void {
    if (!meta?.fromAddr) return;
    store.runSearch('from:' + meta.fromAddr);
    store.backToList();
  }

  function onMenu(a: MenuAction): void {
    switch (a) {
      case 'replyAll':
        reply(true);
        break;
      case 'forward':
        forward();
        break;
      case 'star':
        toggleStar();
        break;
      case 'markUnread':
        void doMarkUnread();
        break;
      case 'move':
        doMove();
        break;
      case 'junk':
        void doJunk();
        break;
      case 'editAsNew':
        void store.editAsNew(folder, uid);
        break;
      case 'addSenderContact':
        void store.addContactQuick(meta?.fromName ?? '', meta?.fromAddr ?? '');
        break;
      case 'fromSender':
        fromSender();
        break;
      case 'muteThread':
        void store.muteThread(uid, folder);
        break;
      case 'print':
        if (typeof window.print === 'function') window.print();
        break;
    }
  }

  function openMenu(ev: MouseEvent): void {
    menuAnchor = anchorFromElement(ev.currentTarget as Element);
    menuOpen = true;
  }

  // ---- labels / mute / thread / viewer ---------------------------------------------------------
  /** `setLabel` posts the action; the flags are re-read from the server so the chips are exact. */
  async function refreshFlags(): Promise<void> {
    try {
      const m = await api.message(folder, uid);
      flags = m.flags ?? [];
      await writeFlags(folder, uid, m.flags ?? []);
    } catch {
      /* the next folder sync brings the flags */
    }
  }

  async function addLabel(): Promise<void> {
    const name = await prompt({ title: t('addLabel'), placeholder: t('labelName'), ok: t('add') });
    if (!name) return;
    if (await store.setLabel([uid], name, true)) await refreshFlags();
  }

  async function removeLabel(name: string): Promise<void> {
    if (await store.setLabel([uid], name, false)) await refreshFlags();
  }

  async function unmute(): Promise<void> {
    const id = muted?.id;
    if (!id) return;
    await store.unmute(id);
    if (!store.mutedThreads.some((m) => m.id === id)) muted = null;
  }

  function toggleThread(): void {
    threadOpenAll = threadOpenAll !== true;
  }

  function openViewer(v: ViewerSpec): void {
    store.viewerOpen = true;
    viewer = v;
  }

  function closeViewer(): void {
    viewer = null;
    store.viewerOpen = false;
  }
</script>

<article class="reader" tabindex="-1" bind:this={article} aria-busy={loading} data-folder={folder} data-uid={uid} data-split={split}>
  <div class="actions">
    <button type="button" class="act" aria-label={t('reply')} onclick={() => reply(false)}><Icon name="reply" size={18} /><span>{t('reply')}</span></button>
    <span class="spacer"></span>
    {#if canArchive}
      <button type="button" class="act icon-only" aria-label={t('archive')} onclick={doArchive}><Icon name="archive" size={18} /></button>
    {/if}
    {#if isSnoozed}
      <button type="button" class="act icon-only rdr-unsnooze" aria-label={t('unsnooze')} onclick={doUnsnooze}><Icon name="inbox" size={18} /></button>
    {:else}
      <button type="button" class="act icon-only rdr-snooze" aria-label={t('snooze')} onclick={doSnooze}><Icon name="moon" size={18} /></button>
    {/if}
    <button type="button" class="act icon-only danger" aria-label={t('del')} onclick={doDelete}><Icon name="trash" size={18} /></button>
    <span class="more-wrap">
      <button type="button" class="act icon-only" aria-haspopup="menu" aria-expanded={menuOpen} aria-label={t('more')} onclick={openMenu}><Icon name="more" size={18} /></button>
      {#if menuOpen && menuAnchor}
        <ReaderMenu anchor={menuAnchor} {flagged} {fromMe} {isJunk} {srcUrl} {dlUrl} onclose={() => (menuOpen = false)} onaction={onMenu} />
      {/if}
    </span>
  </div>

  <div class="rmain">
    {#if meta}
      <header class="head">
        <h1 dir="auto">
          {meta.subject || t('noSubject')}
          {#if thread.length}
            <button type="button" class="texpand" aria-pressed={threadOpenAll === true} aria-label={t(threadOpenAll === true ? 'collapseAll' : 'expandAll')} onclick={toggleThread}>
              <span class="tchev-h" class:up={threadOpenAll === true}><Icon name="chev" size={16} /></span>
            </button>
            <span class="tcount-h" role="note" aria-label={t('inThread', { n: thread.length })}>{thread.length}</span>
          {/if}
        </h1>
      </header>
    {/if}

    {#each older as m (m.key)}
      <ThreadItem {m} {folder} openAll={threadOpenAll} />
    {/each}

    {#if meta}
      <div class="who">
        <span class="avatar">{initialOf(meta.fromName, meta.fromAddr)}</span>
        <div class="names">
          <strong><bdi>{meta.fromName || meta.fromAddr}</bdi></strong>
          {#if meta.fromName}<span class="addr"><bdi>{meta.fromAddr}</bdi></span>{/if}
        </div>
        <time datetime={meta.date}>{formatDate(meta.date)}</time>
      </div>
    {/if}

    {#if meta && auth}
      <AuthBanner {auth} {role} bind:details />
      {#if details}
        <Details {to} {cc} returnPath={auth.returnPath} {messageId} {srcUrl} />
      {/if}
    {/if}

    {#if blocked > 0}
      <ImagesNote onshow={showImages} onalways={alwaysImages} />
    {/if}

    {#if meta && !gone}
      <LabelsBar {labels} onadd={addLabel} onremove={removeLabel} />
    {/if}

    {#if muted}
      <MutedBar onunmute={unmute} />
    {/if}

    {#if unsub}
      <UnsubBar {folder} {uid} {unsub} />
    {/if}

    {#if fromAttached}
      <p class="attached-note" data-kind="from-attached">{t('bodyFromAttached')}</p>
    {/if}

    {#if failed && html === null}
      <p class="fail">{t(failed)}</p>
      {#if gone}
        <button type="button" class="ghost" onclick={() => store.backToList()}>{t('back')}</button>
      {/if}
    {:else if html === null}
      <div class="body-skeleton" aria-label={t('loading')}>
        {#each SKELETON_BARS as w, i (i)}
          {#if w === 0}
            <div style="height:12px"></div>
          {:else}
            <span class="sk" style="width:{w}%"></span>
          {/if}
        {/each}
      </div>
    {:else}
      <MessageBody bind:this={bodyEl} {html} {isHtml} onrendered={onRendered} />
    {/if}

    <Attachments {folder} {uid} {attachments} onpreview={openViewer} />

    {#if newer.length}
      <div class="tafter">
        {#each newer as m (m.key)}
          <ThreadItem {m} {folder} openAll={threadOpenAll} />
        {/each}
      </div>
    {/if}
  </div>
</article>

{#if viewer}
  <Viewer {viewer} onclose={closeViewer} />
{/if}

<EmlPreview />

<style>
  /* svelte-rwh28b (Reader) — the parts rendered by this file; the rest sit in the sub-components */
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    position: sticky;
    top: 0;
    z-index: 5;
    background: var(--surface-card);
    padding: var(--space-3) 0 var(--space-3);
    margin-bottom: var(--space-2);
    border-bottom: 1px solid var(--border-subtle)
  }

  .more-wrap {
    position: relative;
    display: inline-flex
  }

  @media(max-width:480px) {
    .act span {
      display: none
    }
  }

  .act {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: var(--space-2) var(--space-3);
    min-block-size: var(--control-md);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    color: var(--text-body);
    font-size: var(--text-sm);
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .act:hover {
    background: var(--surface-subtle)
  }

  .act:active {
    transform: scale(.98)
  }

  .act.danger:hover {
    color: var(--danger-500);
    border-color: var(--danger-500)
  }

  .spacer {
    flex: 1
  }

  .reader:focus-visible {
    outline-offset: -2px
  }

  .reader {
    height: 100%;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    background: var(--surface-card);
    padding: 0 var(--space-4) var(--space-16)
  }

  .rmain {
    max-inline-size: 72ch;
    margin-inline: auto
  }

  .texpand {
    display: inline-grid;
    place-items: center;
    inline-size: var(--control-sm);
    block-size: var(--control-sm);
    margin-inline-start: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted);
    vertical-align: middle
  }

  .texpand:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .tchev-h {
    display: grid;
    place-items: center
  }

  .tchev-h.up {
    transform: rotate(180deg)
  }

  .tafter {
    margin-top: var(--space-4)
  }

  .tcount-h {
    display: inline-grid;
    place-items: center;
    background: var(--blue-100);
    color: var(--blue-700);
    border-radius: var(--radius-pill);
    font-size: var(--text-eyebrow);
    min-width: 22px;
    block-size: 20px;
    padding-inline: var(--space-2);
    margin-inline-start: var(--space-2);
    vertical-align: middle;
    font-weight: var(--weight-semibold)
  }

  :global(html.tik-dark) .tcount-h {
    background: var(--blue-900);
    color: var(--blue-200)
  }

  .head {
    padding-bottom: var(--space-3);
    margin-bottom: var(--space-3)
  }

  h1 {
    font-size: var(--text-h4);
    line-height: var(--leading-tight);
    color: var(--text-strong);
    unicode-bidi: isolate
  }

  .who {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding-bottom: var(--space-3);
    border-bottom: 1px solid var(--border-subtle);
    margin-bottom: var(--space-4)
  }

  .avatar {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-pill);
    background: var(--blue-100);
    color: var(--blue-700);
    display: grid;
    place-items: center;
    font-weight: var(--weight-semibold);
    flex: none
  }

  :global(html.tik-dark) .avatar {
    background: var(--blue-900);
    color: var(--blue-200)
  }

  .names {
    flex: 1;
    min-width: 0;
    display: grid
  }

  .names strong {
    color: var(--text-strong);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis
  }

  .addr {
    color: var(--text-faint);
    font-size: var(--text-xs);
    unicode-bidi: isolate;
    overflow-wrap: anywhere
  }

  time {
    color: var(--text-faint);
    font-size: var(--text-xs)
  }

  .attached-note {
    margin: var(--space-2) 0 0;
    padding: var(--space-2) var(--space-3);
    border-inline-start: 3px solid var(--border-accent);
    background: var(--surface-sunken);
    color: var(--text-body);
    font-size: var(--text-sm);
    line-height: var(--leading-body);
    border-radius: var(--radius-sm)
  }

  .fail {
    color: var(--text-faint);
    text-align: center;
    padding: var(--space-10)
  }

  .body-skeleton {
    display: grid;
    gap: 10px
  }

  .sk {
    display: block;
    height: 12px;
    background: var(--surface-sunken);
    border-radius: var(--radius-xs);
    animation: svelte-rwh28b-pulse 1.1s var(--ease-standard) infinite alternate
  }

  @keyframes svelte-rwh28b-pulse {
    0% {
      opacity: .55
    }

    to {
      opacity: 1
    }
  }

  /* appended global fixes (2026-09-04) that target the reader */
  h1 {
    text-wrap: balance
  }

  @media (pointer:fine) {
    .reader {
      scrollbar-gutter: stable
    }
  }

  .rmain {
    max-inline-size: none
  }

  .reader {
    padding-inline: var(--space-5)
  }

  @media print {
    .actions {
      display: none
    }
  }
</style>
